import { useCallback, useEffect, useRef, useState } from "react";
import type { PomodoroSettings, TwentySettings, StopwatchSettings } from "../types/timer-settings";
import { useTimerSettings } from "../hooks/use-timer-settings";
import { PomodoroSettingsSection } from "./timer/PomodoroSettingsSection";
import { TwentySettingsSection } from "./timer/TwentySettingsSection";
import { StopwatchSettingsSection } from "./timer/StopwatchSettingsSection";
import { Modal } from "../../../ui/molecules/Modal";

interface TimerSettingsPanelProps {
  readonly onClose: () => void;
}

function SaveToast({ visible }: { readonly visible: boolean }) {
  return (
    <span
      className={`text-xs text-success font-medium transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
    >
      Sauvegarde
    </span>
  );
}

export function TimerSettingsPanel({ onClose }: TimerSettingsPanelProps) {
  const { settings, updatePomodoro, updateTwenty, updateStopwatch, resetAll } = useTimerSettings();
  const [showSaved, setShowSaved] = useState(false);
  const timerRef = useRef<number | null>(null);

  const flash = useCallback(() => {
    setShowSaved(true);
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setShowSaved(false), 1200);
  }, []);

  useEffect(() => {
    return () => { if (timerRef.current !== null) clearTimeout(timerRef.current); };
  }, []);

  const handlePomodoro = useCallback((patch: Partial<PomodoroSettings>) => {
    updatePomodoro(patch);
    flash();
  }, [updatePomodoro, flash]);

  const handleTwenty = useCallback((patch: Partial<TwentySettings>) => {
    updateTwenty(patch);
    flash();
  }, [updateTwenty, flash]);

  const handleStopwatch = useCallback((patch: Partial<StopwatchSettings>) => {
    updateStopwatch(patch);
    flash();
  }, [updateStopwatch, flash]);

  const handleReset = useCallback(() => {
    resetAll();
    flash();
  }, [resetAll, flash]);

  return (
    <Modal
      title="Réglages minuteurs"
      description="Personnalisez vos timers de productivité"
      onClose={onClose}
    >
      <div className="flex flex-col gap-6">
        {/* Header with save indicator */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink">Configuration</span>
          <SaveToast visible={showSaved} />
        </div>

        {/* Timer sections */}
        <div className="flex flex-col gap-6">
          <section>
            <h3 className="text-sm font-medium text-ink mb-3">Pomodoro</h3>
            <PomodoroSettingsSection settings={settings.pomodoro} onUpdate={handlePomodoro} />
          </section>

          <section>
            <h3 className="text-sm font-medium text-ink mb-3">20-20-20</h3>
            <TwentySettingsSection settings={settings.twenty} onUpdate={handleTwenty} />
          </section>

          <section>
            <h3 className="text-sm font-medium text-ink mb-3">Chronomètre</h3>
            <StopwatchSettingsSection settings={settings.stopwatch} onUpdate={handleStopwatch} />
          </section>
        </div>
      </div>

      {/* Footer with reset button */}
      <button
        type="button"
        onClick={handleReset}
        className="self-start px-4 py-2 text-xs font-medium rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
      >
        Réinitialiser tout
      </button>
    </Modal>
  );
}
